<template>
    <div class="hello-micro-page" ref="microPage">
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Lora:ital,wght@0,400;0,500;1,400&family=Amiri:ital,wght@0,400;0,700;1,400&display=swap" rel="stylesheet">

    <!-- ══ HERO ══ -->
    <section class="hero">
      <svg class="hero-bg-circles" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice">
        <circle cx="650" cy="100" r="220"/>
        <circle cx="650" cy="100" r="320"/>
        <circle cx="100" cy="500" r="180"/>
        <circle cx="100" cy="500" r="260"/>
      </svg>

      <div class="lang-selector">
        <button class="lang-btn" :class="{ active: lang === 'en' }" @click="setLang('en')" aria-label="Switch to English">English</button>
        <button class="lang-btn" :class="{ active: lang === 'ar' }" @click="setLang('ar')" aria-label="Switch to Arabic">عربي</button>
      </div>

      <div class="hero-logo">
        <img :src="require('../../assets/tawqin-logo.svg')" alt="tawq.in" class="hero-logo-img" />
        <div class="hero-tagline" v-show="lang === 'en'">say it where you mean it<sup>™</sup></div>
        <div class="hero-tagline" v-show="lang === 'ar'">قُلها حيث تعنيها<sup>™</sup></div>
      </div>

      <h1 class="hero-headline" v-show="lang === 'en'">
        Your voice.<br>Your language.<br><em>Your story.</em>
      </h1>
      <h1 class="hero-headline" v-show="lang === 'ar'">
        صوتك.<br>لغتك.<br><em>حكايتك.</em>
      </h1>

      <p class="hero-sub" v-show="lang === 'en'">
        Add your voice to any video — in any language. Share it with who you choose. No writing required.
      </p>
      <p class="hero-sub" v-show="lang === 'ar'">
        أضف صوتك إلى أي مقطع فيديو — بأي لغة. شاركه مع من تختار. لا حاجة للكتابة.
      </p>

      <div class="hero-mic">
        <div class="mic-ring"></div>
        <div class="mic-ring"></div>
        <div class="mic-body">🎙️</div>
      </div>

      <div class="hero-scroll">
        <span v-show="lang === 'en'">scroll to see how</span>
        <span v-show="lang === 'ar'">مرّر للأسفل لترى كيف</span>
        <div class="scroll-line"></div>
      </div>
    </section>

    <!-- ══ PROBLEM ══ -->
    <section class="problem">
      <div class="inner">
        <div v-show="lang === 'en'">
          <div class="section-label fade-up">The Challenge</div>
          <h2 class="fade-up">Most videos were made<br><em>without your voice.</em></h2>
          <div class="divider fade-up"></div>
          <p class="fade-up">The world is full of videos — but most of them speak in languages your community doesn't use, about places your community has never seen.</p>
          <p class="fade-up">Your language lives in voices, in stories told around fires and at kitchen tables. Not in subtitles. Not in captions written by strangers.</p>
          <div class="salma-block fade-up">
            <div class="salma-name">Salma</div>
            <p class="salma-story">A Hakli mother and grandmother. Ten children raised. She has never written her name — her language has no written form. She is not illiterate. She is <em>non-literate</em>. Her grandchildren learn nursery rhymes in English. They hear her sing — but the words mean nothing to them.</p>
            <p class="salma-question">Can something bridge that distance?</p>
          </div>
          <p class="fade-up">tawq.in was built to answer that question — giving the voice back to you, in your language, told your way.</p>
        </div>
        <div v-show="lang === 'ar'">
          <div class="section-label fade-up">التحدي</div>
          <h2 class="fade-up">معظم مقاطع الفيديو<br>صُنعت <em>بدون صوتك.</em></h2>
          <div class="divider fade-up"></div>
          <p class="fade-up">العالم مليء بمقاطع الفيديو — لكن معظمها يتحدث بلغات لا تعرفها مجتمعاتنا، عن أماكن لم نرها قط.</p>
          <p class="fade-up">لغتنا تعيش في الأصوات، في الحكايات التي تُروى حول النار وعلى موائد البيوت. ليست في ترجمات. ليست في تعليقات كتبها غرباء.</p>
          <div class="salma-block fade-up">
            <div class="salma-name">سلمى</div>
            <p class="salma-story">أمٌّ وجدةٌ حكليّة. ربّت عشرة أبناء. لم تكتب اسمها قط — لغتها لا شكل مكتوب لها. هي ليست أميّة. هي <em>شفهية</em>. يتعلم أحفادها أغاني الأطفال بالإنجليزية. يسمعون صوتها — لكن الكلمات لا تعني لهم شيئاً.</p>
            <p class="salma-question">هل ثمة ما يجسر تلك المسافة؟</p>
          </div>
          <p class="fade-up">tawq.in بُني ليجيب على هذا السؤال — يُعيد الصوت إليك، بلغتك، بطريقتك.</p>
        </div>
      </div>
    </section>

    <!-- ══ REAL STORY ══ -->
    <section class="real-story">
      <div class="inner">
        <div v-show="lang === 'en'">
          <div class="section-label fade-up">A Real Moment</div>
          <h2 class="fade-up">Three women.<br>Three languages.<br><em>One kitchen.</em></h2>
          <div class="divider fade-up"></div>
        </div>
        <div v-show="lang === 'ar'">
          <div class="section-label fade-up">لحظة حقيقية</div>
          <h2 class="fade-up">ثلاث نساء.<br>ثلاث لغات.<br><em>مطبخ واحد.</em></h2>
          <div class="divider fade-up"></div>
        </div>

        <div class="story-body fade-up" v-show="lang === 'en'">
          <p>It was Ramadan. The kitchen smelled incredible.</p>
          <p>Three women had just finished cooking together — one from Oman, one from the Philippines, one from Myanmar. The food was delicious. The communication had been… a challenge.</p>
          <p>So they pointed a camera at the food. The Omani woman described each dish in <strong>Hakli</strong> — a language spoken in the mountains of southern Arabia, with almost no digital presence anywhere on earth.</p>
        </div>
        <div class="story-body fade-up" v-show="lang === 'ar'">
          <p>كان رمضان. كانت رائحة المطبخ رائعة.</p>
          <p>أنهت ثلاث نساء الطهي معاً — واحدة من عُمان، وأخرى من الفلبين، وثالثة من ميانمار. كان الطعام لذيذاً. والتواصل كان… تحدياً.</p>
          <p>فوجّهن الكاميرا نحو الطعام. وصفت المرأة العُمانية كل طبق بلغة <strong>الحكلي</strong> — لغة يتحدثها أقل من 7000 شخص في جبال جنوب الجزيرة العربية، وتكاد تغيب عن الفضاء الرقمي.</p>
        </div>

        <div class="story-video-wrap fade-up">
          <div class="story-video-label" v-show="lang === 'en'">↓ The original video — Hakli food names, spoken aloud</div>
          <div class="story-video-label" v-show="lang === 'ar'">↓ الفيديو الأصلي — أسماء الأطعمة بالحكلي، منطوقة بصوت عالٍ</div>
          <div class="yt-short-frame">
            <iframe
              src="https://www.youtube.com/embed/g7P8875Tk2Y"
              title="Hakli food names — Ramadan kitchen"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              loading="lazy">
            </iframe>
          </div>
        </div>

        <div class="story-moment fade-up">
          <div class="story-moment-image">
            <img :src="require('../../assets/ramadan-moment.png')" alt="Ramadan kitchen moment — Burmese subtitles" />
            <div class="story-moment-caption" v-show="lang === 'en'">Hakli → Arabic → Burmese. Two clicks.</div>
            <div class="story-moment-caption" v-show="lang === 'ar'">حكلي ← عربي ← بورمي. نقرتان.</div>
          </div>
          <div class="story-moment-text">
            <p class="story-pull" v-show="lang === 'en'">Two women who shared no language — suddenly sharing the same moment.</p>
            <p class="story-pull" v-show="lang === 'ar'">امرأتان لا تتشاركان لغة — تتشاركان فجأة نفس اللحظة.</p>
            <p v-show="lang === 'en'">That's what tawq.in does. It doesn't translate — it <em>carries</em> your voice into another language, with your meaning intact.</p>
            <p v-show="lang === 'ar'">هذا ما يفعله tawq.in. لا يترجم — بل <em>يحمل</em> صوتك إلى لغة أخرى، مع الحفاظ على معناك.</p>
          </div>
        </div>

        <div class="story-close fade-up">
          <p v-show="lang === 'en'">The Burmese woman watched the video that afternoon. She didn't know what Hakli was. But she knew what the food was. And she understood — someone had taken the time to speak to her, in her language, about something that mattered.</p>
          <p v-show="lang === 'ar'">شاهدت المرأة البورمية الفيديو في تلك المساء. لم تكن تعرف ما هي الحكلية. لكنها عرفت ما هو الطعام. وفهمت — أن شخصاً ما أخذ الوقت ليتحدث إليها، بلغتها، عن شيء يعنيها.</p>
          <p v-show="lang === 'en'"><strong>That is tawq.in.</strong></p>
          <p v-show="lang === 'ar'"><strong>هذا هو tawq.in.</strong></p>
        </div>
      </div>
    </section>

    <!-- ══ HOW IT WORKS ══ -->
    <section class="steps">
      <div class="inner">
        <div v-show="lang === 'en'">
          <div class="section-label fade-up">How It Works</div>
          <h2 class="fade-up">Three steps.<br><em>Any language.</em></h2>
          <div class="divider fade-up"></div>
        </div>
        <div v-show="lang === 'ar'">
          <div class="section-label fade-up">كيف يعمل</div>
          <h2 class="fade-up">ثلاث خطوات.<br><em>أي لغة.</em></h2>
          <div class="divider fade-up"></div>
        </div>

        <div class="steps-grid">
          <div class="step fade-up">
            <div class="step-icon">🔗</div>
            <div class="step-content">
              <h3 v-show="lang === 'en'">Paste a video link</h3>
              <h3 v-show="lang === 'ar'">الصق رابط فيديو</h3>
              <p v-show="lang === 'en'">YouTube, Vimeo, or any public video. tawq.in loads it instantly.</p>
              <p v-show="lang === 'ar'">يوتيوب، فيميو، أو أي فيديو عام. يحمّله tawq.in فوراً.</p>
            </div>
          </div>
          <div class="step fade-up">
            <div class="step-icon">🎙️</div>
            <div class="step-content">
              <h3 v-show="lang === 'en'">Record your voice</h3>
              <h3 v-show="lang === 'ar'">سجّل صوتك</h3>
              <p v-show="lang === 'en'">Speak in your language. Tap to start, tap to stop. No writing required.</p>
              <p v-show="lang === 'ar'">تحدث بلغتك. اضغط للبدء، واضغط للإيقاف. لا حاجة للكتابة.</p>
            </div>
          </div>
          <div class="step fade-up">
            <div class="step-icon">🌍</div>
            <div class="step-content">
              <h3 v-show="lang === 'en'">Share it</h3>
              <h3 v-show="lang === 'ar'">شاركه</h3>
              <p v-show="lang === 'en'">Anyone with the link hears your voice in sync with the video — in any language you recorded.</p>
              <p v-show="lang === 'ar'">كل من يملك الرابط يسمع صوتك متزامناً مع الفيديو — بأي لغة سجّلت بها.</p>
            </div>
          </div>
        </div>

        <div class="rec-bar fade-up">
          <div class="rec-dot"></div>
          <div class="waveform">
            <span></span><span></span><span></span><span></span>
            <span></span><span></span><span></span><span></span>
          </div>
          <span v-show="lang === 'en'">Recording in Hakli…</span>
          <span v-show="lang === 'ar'">جارٍ التسجيل بالحكلي…</span>
        </div>
      </div>
    </section>

    <!-- ══ TRUST ══ -->
    <section class="trust">
      <div class="inner">
        <div v-show="lang === 'en'">
          <div class="section-label fade-up">In Your Hands</div>
          <h2 class="fade-up">Your voice is yours.<br><em>Always.</em></h2>
          <div class="divider fade-up"></div>
          <p class="fade-up">tawq.in was built with communities who have seen their stories taken, changed, or used without permission. This platform is different — your recordings belong to you, and you set the rules.</p>
        </div>
        <div v-show="lang === 'ar'">
          <div class="section-label fade-up">في يدك</div>
          <h2 class="fade-up">صوتك ملكك.<br><em>دائماً.</em></h2>
          <div class="divider fade-up"></div>
          <p class="fade-up">بُني tawq.in مع مجتمعات رأت قصصها تُؤخذ وتُغيَّر وتُستخدم بدون إذن. هذه المنصة مختلفة — تسجيلاتك ملك لك، وأنت من يضع القواعد.</p>
        </div>

        <div class="trust-cards">
          <div class="trust-card fade-up">
            <div class="icon">🔒</div>
            <h4 v-show="lang === 'en'">Make them private</h4>
            <h4 v-show="lang === 'ar'">اجعلها خاصة</h4>
            <p v-show="lang === 'en'">You choose when and with whom to share your recordings.</p>
            <p v-show="lang === 'ar'">أنت تختار متى ومع من تشارك تسجيلاتك.</p>
          </div>
          <div class="trust-card fade-up">
            <div class="icon">👁️</div>
            <h4 v-show="lang === 'en'">You see who listens</h4>
            <h4 v-show="lang === 'ar'">أنت ترى من يسمع</h4>
            <p v-show="lang === 'en'">Track who has access. Revoke it anytime.</p>
            <p v-show="lang === 'ar'">تابع من يملك صلاحية الوصول. اسحبها في أي وقت.</p>
          </div>
          <div class="trust-card fade-up">
            <div class="icon">🌐</div>
            <h4 v-show="lang === 'en'">No language barrier</h4>
            <h4 v-show="lang === 'ar'">لا حاجة للكتابة</h4>
            <p v-show="lang === 'en'">No writing required to use tawq.in. If you can speak, you can use it.</p>
            <p v-show="lang === 'ar'">لا يتطلب tawq.in الكتابة. إن كنت تتكلم، تستطيع استخدامه.</p>
          </div>
          <div class="trust-card fade-up">
            <div class="icon">📱</div>
            <h4 v-show="lang === 'en'">Works on your phone</h4>
            <h4 v-show="lang === 'ar'">يعمل على هاتفك</h4>
            <p v-show="lang === 'en'">No app to download. Open a browser, start recording.</p>
            <p v-show="lang === 'ar'">لا تطبيق للتحميل. افتح المتصفح وابدأ التسجيل.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ══ LANGUAGES ══ -->
    <section class="languages">
      <div class="inner">
        <div v-show="lang === 'en'">
          <div class="section-label fade-up" style="justify-content:center;">Your Language Belongs Here</div>
          <h2 class="fade-up">Already used in<br><em>many languages.</em></h2>
          <div class="divider fade-up" style="margin:1.5rem auto 0;"></div>
          <p class="fade-up" style="margin-top:1.5rem;">tawq.in works for any spoken language — with any script, or none at all.</p>
        </div>
        <div v-show="lang === 'ar'">
          <div class="section-label fade-up" style="justify-content:center;">لغتك تنتمي هنا</div>
          <h2 class="fade-up">مُستخدَم بالفعل<br><em>في لغات كثيرة.</em></h2>
          <div class="divider fade-up" style="margin:1.5rem auto 0;"></div>
          <p class="fade-up" style="margin-top:1.5rem;">tawq.in يعمل مع أي لغة منطوقة — بأي كتابة، أو بلا كتابة على الإطلاق.</p>
        </div>

        <div class="lang-cloud fade-up">
          <span class="lang-pill featured">Hakli / حكلي</span>
          <span class="lang-pill">Burmese / မြန်မာဘာသာ</span>
          <span class="lang-pill">Spanish / الإسبانية</span>
          <span class="lang-pill">Amazigh / الأمازيغية</span>
          <span class="lang-pill">Bangla / البنغالية</span>
          <span class="lang-pill">Sindhi / السندية</span>
          <span class="lang-pill">Urdu / الأردية</span>
        </div>

        <p class="lang-more fade-up" v-show="lang === 'en'">…and every other language with a voice behind it.</p>
        <p class="lang-more fade-up" v-show="lang === 'ar'">...وكل لغة أخرى يقف وراءها صوت.</p>
      </div>
    </section>

    <!-- ══ CTA ══ -->
    <section class="cta">
      <div class="inner">
        <div v-show="lang === 'en'">
          <div class="section-label fade-up" style="justify-content:center;">Ready?</div>
          <h2 class="fade-up">Start with one video.<br><em>One story.</em></h2>
          <div class="divider fade-up" style="margin:1.5rem auto 0;"></div>
          <p class="fade-up" style="margin-top:1.5rem;">Just open tawq.in, paste a link, and speak. See how your voice sounds in the world.</p>
        </div>
        <div v-show="lang === 'ar'">
          <div class="section-label fade-up" style="justify-content:center;">مستعد؟</div>
          <h2 class="fade-up">ابدأ بفيديو واحد.<br><em>حكاية واحدة.</em></h2>
          <div class="divider fade-up" style="margin:1.5rem auto 0;"></div>
          <p class="fade-up" style="margin-top:1.5rem;">فقط افتح tawq.in، الصق رابطاً، وتكلّم. اسمع كيف يبدو صوتك في العالم.</p>
        </div>

        <div class="fade-up">
          <a href="https://tawq.in/intro/" class="btn-primary" v-show="lang === 'en'">Open tawq.in →</a>
          <a href="https://tawq.in/intro/" class="btn-primary" v-show="lang === 'ar'">← افتح tawq.in</a>
          <br>
          <a href="https://tawq.in/intro/" class="btn-secondary" v-show="lang === 'en'">Learn more about the project</a>
          <a href="https://tawq.in/intro/" class="btn-secondary" v-show="lang === 'ar'">اعرف أكثر عن المشروع</a>
        </div>
      </div>
    </section>

    <!-- ══ FOOTER ══ -->
    <footer>
      <p v-show="lang === 'en'">
        <a href="https://tawq.in/intro/">tawq.in</a> &nbsp;·&nbsp;
        Built for communities whose languages live in voices &nbsp;·&nbsp;
        <a href="https://tawq.in/intro/">طوق</a>
      </p>
      <p v-show="lang === 'ar'">
        <a href="https://tawq.in/intro/">tawq.in</a> &nbsp;·&nbsp;
        بُني لمجتمعات لغاتها تعيش في الأصوات &nbsp;·&nbsp;
        <a href="https://tawq.in/intro/">طوق</a>
      </p>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'Hello',
  metaInfo: {
    title: 'tawq.in — Your Voice. Your Language. Your Story.'
  },
  data() {
    return {
      lang: 'en',
      observer: null,
    }
  },
  methods: {
    setLang(lang) {
      this.lang = lang;
      const page = this.$refs.microPage;
      if (!page) return;
      if (lang === 'ar') {
        page.setAttribute('dir', 'rtl');
      } else {
        page.setAttribute('dir', 'ltr');
      }
      // Re-trigger fade-up animations
      page.querySelectorAll('.fade-up.visible').forEach(el => {
        el.classList.remove('visible');
        setTimeout(() => el.classList.add('visible'), 50);
      });
    },
    initObserver() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const parent = entry.target.parentElement;
            const siblings = Array.from(parent.querySelectorAll('.fade-up:not(.visible)'));
            const idx = siblings.indexOf(entry.target);
            setTimeout(() => {
              entry.target.classList.add('visible');
            }, idx * 80);
            this.observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

      this.$refs.microPage.querySelectorAll('.fade-up').forEach(el => this.observer.observe(el));
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initObserver();
    });
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  }
}
</script>

<style scoped>

.hello-micro-page {
  --sand:  #F5ECD7;
  --ochre: #C8893A;
  --rust:  #A0442A;
  --earth: #5C3D2E;
  --night: #1A1208;
  --sage:  #7A8C6E;
  --mist:  #EBE4D5;
  --gold:  #D4A853;

  background: var(--sand);
  color: var(--earth);
  font-family: 'Lora', Georgia, serif;
  overflow-x: hidden;
}

/* ── ANIMATIONS ── */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(32px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes pulse-ring {
  0%   { transform: scale(1);   opacity: 0.6; }
  100% { transform: scale(1.8); opacity: 0; }
}
@keyframes waveform {
  0%, 100% { transform: scaleY(0.25); }
  50%       { transform: scaleY(1); }
}
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-8px); }
}
@keyframes shimmer {
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
}

.fade-up {
  opacity: 0;
  transform: translateY(32px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}
.fade-up.visible { opacity: 1; transform: translateY(0); }

/* ── LANG SELECTOR ── */
.lang-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 2.5rem;
  animation: fadeUp 0.8s ease 0s both;
  position: relative;
  z-index: 10;
}
.lang-btn {
  padding: 0.55rem 1.5rem;
  font-size: 0.95rem;
  font-family: inherit;
  border: 1.5px solid var(--ochre);
  background: transparent;
  color: var(--ochre);
  cursor: pointer;
  transition: background 0.25s, color 0.25s;
  letter-spacing: 0.05em;
}
.lang-btn:first-child { border-radius: 999px 0 0 999px; border-right: none; }
.lang-btn:last-child  { border-radius: 0 999px 999px 0; border-left: none; font-family: 'Amiri', serif; font-size: 1.05rem; }
.lang-btn.active { background: var(--ochre); color: white; }
.lang-btn:not(.active):hover { background: rgba(200,137,58,0.12); }

/* ── HERO ── */
.hero {
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem 4rem;
  position: relative;
  overflow: hidden;
  background: radial-gradient(ellipse at 30% 60%, #c8893a22 0%, transparent 60%),
              radial-gradient(ellipse at 80% 20%, #a0442a18 0%, transparent 50%),
              var(--sand);
  text-align: center;
}
.hero-bg-circles {
  position: absolute;
  inset: 0;
  pointer-events: none;
  width: 100%;
  height: 100%;
}
.hero-bg-circles circle { fill: none; stroke: var(--ochre); stroke-width: 1; opacity: 0.08; }
.hero-logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 2rem;
  animation: fadeUp 1s ease 0.1s both;
}
.hero-logo-img { width: clamp(180px, 35vw, 280px); height: auto; }
.hero-tagline {
  font-family: 'Lora', Georgia, serif;
  font-size: clamp(0.85rem, 2vw, 1.05rem);
  font-style: italic;
  color: var(--ochre);
  letter-spacing: 0.08em;
  opacity: 0.9;
}
.hero-tagline sup { font-size: 0.6em; vertical-align: super; font-style: normal; letter-spacing: 0; }
.hero-headline {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.8rem, 8vw, 6rem);
  font-weight: 700;
  line-height: 1.15;
  color: var(--night);
  max-width: 820px;
  animation: fadeUp 1s ease 0.3s both;
}
.hero-headline em { font-style: italic; color: var(--rust); }
.hero-sub {
  font-size: clamp(1.05rem, 2.5vw, 1.3rem);
  color: var(--earth);
  max-width: 520px;
  margin-top: 1.8rem;
  line-height: 1.8;
  animation: fadeUp 1s ease 0.5s both;
}
.hero-mic {
  margin-top: 3rem;
  position: relative;
  width: 90px;
  height: 90px;
  animation: fadeUp 1s ease 0.7s both, float 4s ease-in-out 2s infinite;
}
.mic-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--ochre);
  opacity: 0.15;
  animation: pulse-ring 2.5s ease-out 1.5s infinite;
}
.mic-ring:nth-child(2) { animation-delay: 3s; }
.mic-body {
  position: absolute;
  inset: 10px;
  background: var(--rust);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  box-shadow: 0 8px 32px #a0442a44;
}
.hero-scroll {
  margin-top: 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: var(--ochre);
  font-size: 0.8rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  animation: fadeUp 1s ease 1s both;
  opacity: 0.7;
}
.scroll-line { width: 1px; height: 40px; background: linear-gradient(to bottom, var(--ochre), transparent); }

/* ── SECTIONS ── */
section { padding: 5rem 2rem; }
.inner { max-width: 760px; margin: 0 auto; }

.section-label {
  font-size: 0.78rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ochre);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.section-label::before {
  content: '';
  display: block;
  width: 28px;
  height: 1px;
  background: var(--ochre);
  flex-shrink: 0;
}

h2 {
  font-family: 'Playfair Display', serif;
  font-size: clamp(2rem, 5vw, 3rem);
  line-height: 1.2;
  color: var(--night);
  margin-bottom: 1.5rem;
}
h2 em { color: var(--rust); font-style: italic; }

p {
  font-size: clamp(1rem, 2vw, 1.15rem);
  line-height: 1.85;
  color: var(--earth);
  margin-bottom: 1.2rem;
}

.divider {
  width: 60px;
  height: 2px;
  background: linear-gradient(to right, var(--ochre), transparent);
  margin: 1.5rem 0 2rem;
}

/* ── PROBLEM ── */
.problem { background: var(--night); color: var(--sand); position: relative; overflow: hidden; }
.problem::before {
  content: '';
  position: absolute;
  top: -100px; left: -100px;
  width: 400px; height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, #c8893a18, transparent 70%);
  pointer-events: none;
}
.problem h2 { color: var(--sand); }
.problem h2 em { color: var(--gold); }
.problem p { color: #c8b99a; }
.problem .section-label { color: var(--gold); }
.problem .section-label::before { background: var(--gold); }

.salma-block {
  margin: 2.5rem 0;
  padding: 2rem 2rem 2rem 2.5rem;
  border-left: 3px solid var(--gold);
  background: rgba(255,255,255,0.03);
  border-radius: 0 8px 8px 0;
}
.salma-name {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.3rem, 3vw, 1.7rem);
  color: var(--gold);
  font-style: italic;
  margin-bottom: 1rem;
  letter-spacing: 0.04em;
}
.salma-story { font-size: clamp(1rem, 2.2vw, 1.15rem) !important; color: #c8b99a !important; line-height: 1.85 !important; margin-bottom: 1rem !important; }
.salma-story em { color: var(--gold); font-style: italic; }
.salma-question { font-size: clamp(1rem, 2.2vw, 1.15rem) !important; color: var(--sand) !important; font-style: italic; line-height: 1.7 !important; margin: 0 !important; opacity: 0.9; }

/* ── REAL STORY ── */
.real-story {
  background: linear-gradient(160deg, #1a1208 0%, #2d1f0e 50%, #1a1208 100%);
  color: var(--sand);
  position: relative;
  overflow: hidden;
  padding: 5rem 2rem 6rem;
}
.real-story .section-label { color: var(--gold); }
.real-story .section-label::before { background: var(--gold); }
.real-story h2 { color: var(--sand); }
.real-story h2 em { color: var(--gold); }
.real-story .divider { background: linear-gradient(to right, var(--gold), transparent); }

.story-body p { color: #d4c4a8; font-size: clamp(1rem, 2vw, 1.15rem); line-height: 1.9; margin-bottom: 1rem; }
.story-body strong { color: var(--gold); font-weight: 600; }

.story-video-wrap { margin: 2rem 0; }
.story-video-label {
  font-size: 0.8rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--gold);
  opacity: 0.75;
  margin-bottom: 0.75rem;
}
.yt-short-frame {
  position: relative;
  width: 100%;
  max-width: 340px;
  aspect-ratio: 9/16;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0,0,0,0.5), 0 0 0 1px rgba(200,137,58,0.2);
  background: #000;
}
.yt-short-frame iframe { position: absolute; inset: 0; width: 100%; height: 100%; }

.story-moment {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin: 3rem 0 2rem;
}
.story-moment-image img {
  width: 100%;
  max-width: 280px;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(200,137,58,0.25);
  display: block;
}
.story-moment-caption {
  font-size: 0.78rem;
  color: var(--gold);
  opacity: 0.7;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-top: 0.75rem;
}
.story-moment-text p { color: #d4c4a8; font-size: clamp(1rem, 2vw, 1.15rem); line-height: 1.85; margin-bottom: 1rem; }
.story-moment-text em { color: var(--gold); font-style: italic; }
.story-pull {
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.3rem, 3vw, 1.7rem) !important;
  color: var(--sand) !important;
  line-height: 1.4 !important;
  margin-bottom: 1.2rem !important;
}
.story-close { border-top: 1px solid rgba(200,137,58,0.2); padding-top: 2rem; margin-top: 1rem; }
.story-close p { color: #d4c4a8; font-size: clamp(1.05rem, 2.2vw, 1.2rem); line-height: 1.8; }
.story-close strong { color: var(--gold); }

@media (max-width: 640px) {
  .story-moment { grid-template-columns: 1fr; gap: 2rem; }
  .yt-short-frame { max-width: 280px; }
}

/* ── STEPS ── */
.steps { background: var(--sand); }
.steps-grid { margin-top: 3rem; }
.step { display: flex; gap: 2rem; align-items: flex-start; padding: 2rem 0; position: relative; }
.step:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 31px;
  top: 0; bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, var(--ochre), transparent);
  opacity: 0.3;
}
.step-icon {
  flex-shrink: 0;
  width: 64px; height: 64px;
  border-radius: 50%;
  background: var(--ochre);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  box-shadow: 0 4px 16px #c8893a33;
  position: relative;
  z-index: 1;
}
.step-content h3 { font-family: 'Playfair Display', serif; font-size: 1.4rem; color: var(--night); margin-bottom: 0.5rem; }
.step-content p { font-size: 1rem; margin: 0; color: var(--earth); line-height: 1.75; }

.rec-bar {
  margin: 1.5rem 0 0;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: var(--rust);
  border-radius: 40px;
  color: white;
  font-size: 0.9rem;
  max-width: 320px;
  box-shadow: 0 8px 24px #a0442a44;
}
.rec-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  background: #ff6b6b;
  animation: pulse-ring 1.2s ease-out infinite;
  flex-shrink: 0;
}
.waveform { display: flex; align-items: center; gap: 3px; flex: 1; }
.waveform span {
  display: block;
  width: 3px; height: 28px;
  background: rgba(255,255,255,0.7);
  border-radius: 2px;
  transform-origin: center;
  will-change: transform;
  animation: waveform 0.8s ease-in-out infinite;
}
.waveform span:nth-child(2) { animation-delay: 0.1s; }
.waveform span:nth-child(3) { animation-delay: 0.2s; }
.waveform span:nth-child(4) { animation-delay: 0.15s; }
.waveform span:nth-child(5) { animation-delay: 0.3s; }
.waveform span:nth-child(6) { animation-delay: 0.05s; }
.waveform span:nth-child(7) { animation-delay: 0.25s; }
.waveform span:nth-child(8) { animation-delay: 0.35s; }

/* ── TRUST ── */
.trust { background: linear-gradient(135deg, #3d2416 0%, #1A1208 100%); color: var(--sand); position: relative; overflow: hidden; }
.trust h2 { color: var(--sand); }
.trust h2 em { color: var(--gold); }
.trust p { color: #c8b99a; }
.trust .section-label { color: var(--gold); }
.trust .section-label::before { background: var(--gold); }
.trust-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.2rem; margin-top: 2.5rem; }
.trust-card {
  padding: 1.5rem;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(200,137,58,0.2);
  border-radius: 12px;
  text-align: center;
  transition: background 0.3s, transform 0.3s;
}
.trust-card:hover { background: rgba(200,137,58,0.08); transform: translateY(-4px); }
.trust-card .icon { font-size: 2rem; margin-bottom: 0.8rem; }
.trust-card h4 { font-family: 'Playfair Display', serif; font-size: 1.05rem; color: var(--sand); margin-bottom: 0.5rem; }
.trust-card p { font-size: 0.88rem; margin: 0; color: #9a8870; line-height: 1.6; }

/* ── LANGUAGES ── */
.languages { background: var(--mist); text-align: center; }
.lang-cloud { margin: 2rem auto 0; display: flex; flex-wrap: wrap; justify-content: center; gap: 0.8rem; max-width: 640px; }
.lang-pill {
  padding: 0.5rem 1.2rem;
  border-radius: 999px;
  font-size: 0.9rem;
  background: white;
  color: var(--earth);
  border: 1px solid rgba(90,50,20,0.12);
  box-shadow: 0 2px 8px rgba(90,50,20,0.06);
  transition: all 0.2s;
  cursor: default;
}
.lang-pill:hover { background: var(--ochre); color: white; border-color: var(--ochre); transform: scale(1.05); }
.lang-pill.featured { background: var(--rust); color: white; border-color: var(--rust); font-weight: 500; }
.lang-more { font-size: 0.85rem; color: var(--sage); margin-top: 1.5rem; font-style: italic; }

/* ── CTA ── */
.cta { background: var(--sand); text-align: center; padding: 6rem 2rem; }
.cta h2 { text-align: center; }
.cta p { text-align: center; max-width: 480px; margin: 0 auto 2.5rem; }

.btn-primary {
  display: inline-block;
  padding: 1.1rem 2.8rem;
  background: var(--rust);
  color: white;
  font-family: 'Lora', serif;
  font-size: 1.05rem;
  text-decoration: none;
  border-radius: 999px;
  box-shadow: 0 8px 28px #a0442a44;
  transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
  position: relative;
  overflow: hidden;
}
.btn-primary:hover { transform: translateY(-3px); box-shadow: 0 14px 36px #a0442a55; background: var(--earth); }
.btn-secondary {
  display: inline-block;
  margin-top: 1.2rem;
  color: var(--ochre);
  font-size: 0.9rem;
  text-decoration: none;
  letter-spacing: 0.05em;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s;
}
.btn-secondary:hover { border-color: var(--ochre); }

/* ── FOOTER ── */
footer {
  background: var(--night);
  color: #6b5c4a;
  text-align: center;
  padding: 2.5rem 2rem;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
}
footer a { color: var(--gold); text-decoration: none; }
footer a:hover { text-decoration: underline; }

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
}
</style>
